'use client';

import React from 'react';

export type StepId = 'choose_play' | 'configure' | 'preview';

export interface Step {
  id: StepId;
  label: string;
  number: number;
}

export const STEPS: Step[] = [
  { id: 'choose_play', label: 'Choose Play', number: 1 },
  { id: 'configure', label: 'Configure', number: 2 },
  { id: 'preview', label: 'Preview & Publish', number: 3 },
];

interface StepperProps {
  currentStep: StepId;
  onStepChange: (step: StepId) => void;
  completedSteps?: StepId[];
}

const Stepper = ({
  currentStep,
  onStepChange,
  completedSteps = [],
}: StepperProps) => {
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  const getStepState = (step: Step, index: number) => {
    if (step.id === currentStep) return 'active';
    if (completedSteps.includes(step.id) || index < currentIndex)
      return 'completed';
    return 'upcoming';
  };

  return (
    <nav aria-label="Progress" data-testid="strategy-builder-stepper">
      <ol className="flex items-center gap-2">
        {STEPS.map((step, index) => {
          const state = getStepState(step, index);
          const isClickable = state === 'completed' || state === 'active';

          return (
            <li key={step.id} className="flex items-center">
              <button
                type="button"
                onClick={() => isClickable && onStepChange(step.id)}
                disabled={!isClickable}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  state === 'active'
                    ? 'bg-sky-600 text-white'
                    : state === 'completed'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'cursor-not-allowed bg-neutral-100 text-tertiary'
                }`}
                aria-current={state === 'active' ? 'step' : undefined}
                data-testid={`step-${step.id}`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    state === 'active'
                      ? 'bg-white/20 text-white'
                      : state === 'completed'
                      ? 'bg-green-500 text-white'
                      : 'bg-neutral-050 text-tertiary'
                  }`}
                >
                  {step.number}
                </span>
                <span className="hidden sm:inline">{step.label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={`mx-2 h-px w-8 ${
                    index < currentIndex ? 'bg-primary' : 'bg-neutral-300'
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Stepper;
